<template>
    <div class="applications">
        <Panel>
            <h2>Welcome {{ fullName }}</h2>

            <p>
                This page allows you to log into the applications you have access to or to modify your account data.
            </p>

            <h3>Applications</h3>

            <p>You can log into the specific application by clicking on it below.</p>

            <div class="text-right app-buttons">
                <button class="btn btn-primary btn-sm" @click="refreshList"><i class="fas fa-sync-alt"></i> Refresh
                </button>
            </div>

            <TabList :tab-map="tabMap" :selected-key="selectedTab" @tabChanged="handleTabChanged">
                <template #app_list>
                    <div class="app-list">
                        <Loader v-if="isLoading" wait-text="Loading application list..."/>
                        <div v-else>
                            <Alert
                                v-if="applications.length === 0"
                                type="warning"
                                message="You do not have any applications assigned to you. Please contact your administrator to allow you access to the applications you require."
                            />
                            <a class="application" :href="`/app/login?appId=${app.identifier}`"
                               v-for="app in applications"
                               :key="app.identifier">
                                <img src="/img/emsmc/logo_only.svg" :alt="app.name" width="30" height="30"/>
                                <span>{{ app.name }}</span>
                            </a>
                        </div>
                    </div>
                </template>
                <template #external_app_list>
                    <div class="app-list">
                        <Loader v-if="isLoading" wait-text="Loading external application list..."/>
                        <div v-else>
                            <Alert
                                v-if="externalApplications.length === 0"
                                type="warning"
                                message="No external applications available."
                            />
                            <a class="application" :href="`/app/external-login?appId=${app.identifier}`"
                               v-for="app in externalApplications"
                               :key="app.identifier">
                                <img src="/img/emsmc/logo_only.svg" :alt="app.name" width="30" height="30"/>
                                <span>{{ app.name }}</span>
                            </a>
                        </div>
                    </div>
                </template>
            </TabList>
        </Panel>
    </div>

</template>

<script>
import { computed, onMounted, ref } from "vue";
import { getFullUserName } from "@/stores/user";
import Panel from "@/components/Panel";
import { useLoading } from "@/hooks/use-loading";
import Loader from "@/components/Loader";
import Alert from "@/components/Alert";
import { getAssignedApplications } from "@/api/app";
import TabList from "@/components/TabList.vue";
import { getAssignedExternalApplications } from "@/api/external-app";

export default {
    name: "ApplicationsPanel",
    components: { TabList, Alert, Loader, Panel },
    setup() {
        const { isLoading, startLoading, stopLoading } = useLoading(false);

        const isAppListLoaded = ref(false);
        const isExternalAppListLoaded = ref(false);

        const selectedTab = ref('app_list');
        const tabMap = {
            app_list: 'General',
            external_app_list: 'External',
        };

        const applications = ref([]);
        const externalApplications = ref([]);

        const refreshList = async () => {
            if (selectedTab.value === 'app_list') {
                isAppListLoaded.value = false;
                await reloadAppList();
            } else {
                isExternalAppListLoaded.value = false;
                await reloadExternalAppList();
            }
        };

        const handleTabChanged = async (tab) => {
            selectedTab.value = tab;
            if (tab === 'app_list') {
                await reloadAppList();
            } else {
                await reloadExternalAppList();
            }
        }

        const reloadAppList = async () => {
            if (isAppListLoaded.value) {
                return;
            }

            try {
                startLoading();
                const { data } = await getAssignedApplications();
                applications.value = data.result;
                isAppListLoaded.value = true;
            } catch (e) {
                console.error(e);
            } finally {
                stopLoading();
            }
        }

        const reloadExternalAppList = async () => {
            if (isExternalAppListLoaded.value) {
                return;
            }

            try {
                startLoading();
                const { data } = await getAssignedExternalApplications();
                externalApplications.value = data.result;
                isExternalAppListLoaded.value = true;
            } catch (e) {
                console.error(e);
            } finally {
                stopLoading();
            }
        }

        onMounted(reloadAppList);

        return {
            isLoading,
            applications,
            externalApplications,
            selectedTab,
            tabMap,
            refreshList,
            fullName: computed(() => getFullUserName()),
            handleTabChanged,
        }
    }
}
</script>

<style scoped lang="scss">
.applications {
    .app-buttons {
        padding-bottom: 8px;
    }

    .application {
        border-bottom: 1px solid #CCC;
        padding: 10px;
        display: block;
        color: #343;

        &:last-child {
            border-bottom: none;
        }

        img {
            display: inline-block;
            margin-right: 15px;
        }

        span {
            font-size: 18px;
            vertical-align: middle;
        }

        &:hover {
            cursor: pointer;
            background: #94c632;
            color: #FFF;
        }
    }
}
</style>